import { Chip, IconButton, Menu, Tooltip } from "@itk/mui-components";
import { getClickableProps } from "src/utils/keyboard-utils";
import { Box, Checkbox, Stack, Typography, useTheme, Zoom } from "@mui/material";
import { MouseEvent, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ChipList, DekoxIcon, TruncatedText } from "src/components";
import { genericTranslationsK } from "src/translation-hooks/generic-translations";
import { ROUTES_URL } from "src/utils/constants";
import { connect } from "react-redux";
import { productInformationActions } from "../../../product-information-dialog/product-information-dialog-store";
import {
  Model_ConfigurationInvalidInformationGet,
  Model_ConfigurationMissingInformationGet,
  Model_ConfigurationsGet_SystemProductConfigurationItem,
  Model_LabelAndReferenceGet,
  Shared_SystemSettings
} from "src/backend/openapi/v1";
import { applicationActions } from "src/components/application-store";
import { configuratorActions } from "src/pages/configurator/configurator-store";
import { useConfiguratorTranslations } from "src/pages/configurator/configurator-translations";
import { TConfigureProductSearchParams } from "src/pages/configure-product/configure-product-types";
import { getProductChipLabels, getProductDisplayName } from "src/pages/configurator/configurator-utils";
import { RootState } from "src/store";
import { CopyMenu } from "../../../copy-menu";
import { PRODUCT_NAME_FALLBACK_WIDTH, PRODUCT_NAME_WIDTH_VARIABLE } from "../../configurations-list-utils";

// Deliberately reads neither `labels` nor `selectedProducts` — see the labelIndex and isSelected
// props below. Both were whole-collection subscriptions that re-rendered every mounted row (and
// re-scanned the full labels array per row) whenever one selection or one label changed.
const mapStateToProps = (state: RootState) => ({
  viewSettings: state.pageReducer.combinedConfiguratorReducer.viewSettingsReducer.applied,
  copyConfigurationItemsLimit: parseInt(state.generalReducer.applicationReducer.systemSettings[Shared_SystemSettings.CopyConfigurationItemsLimit] ?? "", 10)
});

const ownActions = {
  setIsProductInformationDialogOpen: productInformationActions.setIsProductInformationDialogOpen,
  deleteProjectLineItemsRequest: configuratorActions.deleteProjectLineItemsRequest,
  copyProjectLineItemsRequest: configuratorActions.copyProjectLineItemsRequest,
  getConfigurationsRequest: configuratorActions.getConfigurationsRequest,
  getLabelsRequest: configuratorActions.getLabelsRequest,
  toggleSelectedProduct: configuratorActions.toggleSelectedProduct,
  setWarningAction: applicationActions.setWarningAction
};

type Props = typeof ownActions &
  ReturnType<typeof mapStateToProps> & {
    missingInformation: Model_ConfigurationMissingInformationGet[];
    invalidInformation: Model_ConfigurationInvalidInformationGet[];
    projectLineItem: Model_ConfigurationsGet_SystemProductConfigurationItem;
    isProductTab?: boolean;
    systemProductConfigurationName?: string;
    configurationId: string;
    // Only rendered when provided — callers that don't pass it (e.g. products.tsx) keep today's row unchanged
    onAddChild?: () => void;
    canAddChild?: boolean;
    // Copy is only offered one level below a root (this item's parent is itself a root) — defaults to
    // false so callers that don't compute depth (e.g. products.tsx) keep today's row unchanged
    canCopy?: boolean;
    // Separate from isProductTab, which only picks the card background — the grouped view omits that
    // prop but still needs checkboxes
    isSelectable?: boolean;
    // Built once per list (buildLabelIndex) and shared by every row, replacing a per-row O(labels ×
    // references) scan that also re-ran for every row on any labels change.
    labelIndex: Map<string, Model_LabelAndReferenceGet[]>;
    // Resolved by the parent against a Set — see mapStateToProps above for why it isn't read here.
    isSelected?: boolean;
  };

const ProductConfigurationBase = (props: Props) => {
  const {
    setIsProductInformationDialogOpen,
    projectLineItem,
    setWarningAction,
    deleteProjectLineItemsRequest,
    copyProjectLineItemsRequest,
    getConfigurationsRequest,
    getLabelsRequest,
    isProductTab = false,
    systemProductConfigurationName,
    labelIndex,
    viewSettings,
    missingInformation,
    invalidInformation,
    configurationId,
    onAddChild,
    canAddChild = false,
    canCopy = false,
    isSelectable = false,
    isSelected = false,
    toggleSelectedProduct,
    copyConfigurationItemsLimit
  } = props;
  const theme = useTheme();
  const navigate = useNavigate();

  // Resolved once for the whole page instead of per row — see configurator-translations.tsx
  const { configuratorPageT, genericTranslationsT } = useConfiguratorTranslations();

  const { projectId = "" } = useParams<{ projectId: string }>();

  const [copyBtnAnchor, setCopyBtnAnchor] = useState<HTMLButtonElement | null>(null);

  const hasValidCopyLimit = Number.isInteger(copyConfigurationItemsLimit) && copyConfigurationItemsLimit >= 1;

  // systemProductConfigurationItemId on missing/invalid info; systemProductConfigurationId on the item itself
  const missingInformationsForProjectLineItem = useMemo(
    () => missingInformation?.filter(m => m.systemProductConfigurationItemId === projectLineItem.systemProductConfigurationItemId) ?? [],
    [missingInformation, projectLineItem.systemProductConfigurationItemId]
  );

  const invalidInformationsForProjectLineItem = useMemo(
    () => invalidInformation?.filter(i => i.systemProductConfigurationItemId === projectLineItem.systemProductConfigurationItemId) ?? [],
    [invalidInformation, projectLineItem.systemProductConfigurationItemId]
  );

  // Both sides now derive from the same persisted IsValid flag; invalidInformation is kept as a
  // fallback for render paths where the item's own flag is absent.
  const isProjectLineItemInvalid = projectLineItem.isValid === false || invalidInformationsForProjectLineItem.length > 0;

  const statusIconCount = (missingInformationsForProjectLineItem.length > 0 ? 1 : 0) + (isProjectLineItemInvalid ? 1 : 0);

  const chipSx = useMemo(
    () => ({
      bgcolor: theme.palette.customColors.lightGrey,
      ml: 1,
      borderRadius: 1,
      border: `1px solid ${theme.palette.customColors.grey2}`
    }),
    [theme]
  );

  const cardSx = useMemo(
    () => ({
      borderRadius: 1,
      border: `1px solid ${theme.palette.customColors.grey}`,
      backgroundColor: !isProductTab ? theme.palette.customColors.lightGrey2 : theme.palette.customColors.white,
      cursor: "pointer",
      outline: "none",
      "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}` },
      // At rest the actions container is 0-width so the row's other content gets the full width; on
      // hover it grows into view instead of always reserving the space — matches configure-product's
      // product-line-item-row. Sized for up to 4 buttons (add + copy + edit + delete).
      "&:hover .product-configuration-item-actions": { maxWidth: 170, ml: 1 },
      // The status slot keeps a fixed two-icon width at rest so the SPC chip lands at the same
      // x-position on every row. On hover it shrinks to only the icons actually rendered, so the
      // empty slots hand their space to the sliding actions while every visible icon keeps its own.
      "&:hover .product-configuration-item-status": { width: `calc(${statusIconCount} * (1.25rem + ${theme.spacing(2)}))` }
    }),
    [isProductTab, theme, statusIconCount]
  );

  // configurationId prop is passed from the parent; systemProductConfigurationId is the item ID
  const navigateToConfigureProduct = () => {
    if (configurationId && projectLineItem.systemProductConfigurationItemId) {
      navigate({
        pathname: ROUTES_URL.CONFIGURE_PRODUCT.build(projectId, configurationId),
        search: `?${new URLSearchParams({ projectLineItemId: projectLineItem.systemProductConfigurationItemId } as TConfigureProductSearchParams).toString()}`
      });
    }
  };

  const onEdit = (e: MouseEvent) => {
    e.stopPropagation();
    navigateToConfigureProduct();
  };

  const onDelete = (e: MouseEvent) => {
    e.stopPropagation();
    setWarningAction({
      confirm: () => {
        if (!projectLineItem.systemProductConfigurationItemId) return;

        // systemProductConfigurationItemIds replaces projectLineItemIds in the bulk delete payload
        deleteProjectLineItemsRequest({ systemProductConfigurationItemIds: [projectLineItem.systemProductConfigurationItemId] }, isSuccess => {
          if (isSuccess) {
            getConfigurationsRequest(projectId);
          }
        });
        setWarningAction(undefined);
      },
      dialogProps: {
        message: configuratorPageT.areYouSureYouWantToDeleteThisProductConfiguration,
        confirmButtonTranslation: {
          t: genericTranslationsT,
          k: genericTranslationsK,
          field: "delete"
        }
      },
      variant: "confirmCancel"
    });
  };

  const handleCopy = (blankValues: boolean, count: number) => {
    setCopyBtnAnchor(null);
    if (!projectLineItem.systemProductConfigurationItemId) return;

    copyProjectLineItemsRequest(blankValues, { systemProductConfigurationItemIds: [projectLineItem.systemProductConfigurationItemId], count }, isSuccess => {
      if (isSuccess) {
        getConfigurationsRequest(projectId);
        getLabelsRequest(projectId);
      }
    });
  };

  const handleOpenProduct = () => {
    if (!projectLineItem.productId) return;
    setIsProductInformationDialogOpen({ productId: projectLineItem.productId });
  };

  // configurationId prop is passed separately from the parent — no longer on the item model itself
  const configurationLabels = useMemo(() => (configurationId ? (labelIndex.get(configurationId) ?? []) : []), [labelIndex, configurationId]);

  // Entity labels, the product type and attribute-value chips merged into a single ChipList so they
  // share one overflow ("+N more") computation. Derived by getProductChipLabels, which the list also
  // calls to work out how much width the labels need before it sizes the shared name column.
  // Memoized because ChipList compares its labels prop by reference.
  const mergedChipLabels = useMemo(() => getProductChipLabels(projectLineItem, labelIndex, viewSettings.selectedItemIds), [projectLineItem, labelIndex, viewSettings.selectedItemIds]);

  return (
    <>
      <Stack id={`${configurationId}-${projectLineItem.systemProductConfigurationItemId}`} direction="row" {...getClickableProps(handleOpenProduct)} sx={cardSx} onClick={handleOpenProduct}>
        <Stack gap={2} direction="row" alignItems="center" width="100%" overflow="hidden" px={1.5} py={1}>
          {isSelectable && (
            <Checkbox
              size="small"
              // Default small-checkbox padding would make it the tallest thing in the row
              sx={{ flex: "none", p: 0.5, ml: -0.5 }}
              checked={isSelected}
              onClick={e => e.stopPropagation()}
              onChange={e => {
                e.stopPropagation();
                const itemId = projectLineItem.systemProductConfigurationItemId;
                if (!itemId) return;
                toggleSelectedProduct(itemId);
              }}
            />
          )}

          {/* Product name — one width shared by every row in the list, published as a CSS variable by its parent.
              flex="none" keeps it out of the hover animation below, which must steal only from the chips column. */}
          <Stack flex="none" minWidth={0} width={`var(${PRODUCT_NAME_WIDTH_VARIABLE}, ${PRODUCT_NAME_FALLBACK_WIDTH}px)`}>
            <TruncatedText variant="subtitle2" fontWeight="500" text={getProductDisplayName(projectLineItem)} />
          </Stack>

          {/* Chips */}
          <Stack flex={1} direction="row" alignItems="center" gap={1} overflow="hidden">
            {projectLineItem?.systemProductConfigurationItemId && mergedChipLabels.length > 0 && <ChipList labels={mergedChipLabels} />}
          </Stack>

          {/* Action buttons */}
          <Stack flex="none" direction="row" alignItems="center" justifyContent="flex-end">
            {systemProductConfigurationName && configurationId && (
              <Tooltip
                title={
                  configurationLabels.length > 0 && (
                    <Stack sx={{ gap: 0.5 }}>
                      {configurationLabels.map((label, index) => (
                        <Typography key={index} variant="caption" color="text.primary">
                          {label.shortname}
                        </Typography>
                      ))}
                    </Stack>
                  )
                }
                placement="bottom-start"
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      boxShadow: theme.shadows[3],
                      border: `1px solid ${theme.palette.customColors.grey2}`,
                      p: 1.5
                    }
                  }
                }}
              >
                <span onClick={e => e.stopPropagation()}>
                  <Chip label={systemProductConfigurationName} size="small" sx={{ ...chipSx, cursor: "default" }} />
                </span>
              </Tooltip>
            )}

            {/* Fixed to exactly two icon slots so the SPC chip always lands at the same x-position;
                only the active icon(s) are mounted, packed against the right edge. On row hover it
                shrinks to the mounted icons (see cardSx) so unused slots leave no gap behind. */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="flex-end"
              className="product-configuration-item-status"
              sx={{ width: `calc(2 * (1.25rem + ${theme.spacing(2)}))`, flexShrink: 0, overflow: "hidden", transition: "width 0.4s" }}
            >
              {missingInformationsForProjectLineItem.length > 0 && (
                <Tooltip
                  title={
                    <Box>
                      <Box>{configuratorPageT.productIsMissingInformation}</Box>
                      <Box component="ul" sx={{ m: 0, pl: 2 }}>
                        {missingInformationsForProjectLineItem.map((info, index) => (
                          <li key={index}>{info.attributeDescription}</li>
                        ))}
                      </Box>
                    </Box>
                  }
                  arrow
                  placement="bottom"
                  slots={{ transition: Zoom }}
                >
                  <DekoxIcon icon="info" fontSize="small" color="warning" onClick={onEdit} {...getClickableProps(navigateToConfigureProduct)} sx={{ mx: 1, cursor: "pointer" }} />
                </Tooltip>
              )}

              {isProjectLineItemInvalid && (
                <Tooltip
                  title={
                    <Box>
                      <Box>{configuratorPageT.productHasInvalidInformation}</Box>
                    </Box>
                  }
                  arrow
                  placement="bottom"
                  slots={{ transition: Zoom }}
                >
                  <DekoxIcon icon="errorOutline" fontSize="small" color="error" onClick={onEdit} {...getClickableProps(navigateToConfigureProduct)} sx={{ mx: 1, cursor: "pointer" }} />
                </Tooltip>
              )}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap={0.5}
              className="product-configuration-item-actions"
              sx={{ maxWidth: 0, ml: 0, overflow: "hidden", flexShrink: 0, transition: "max-width 0.4s, margin-left 0.4s" }}
            >
              {onAddChild && canAddChild && (
                <IconButton
                  k={genericTranslationsK.add}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    onAddChild();
                  }}
                  sx={{ color: theme.palette.customColors.black }}
                >
                  <DekoxIcon icon="add" fontSize="small" />
                </IconButton>
              )}
              {canCopy && (
                // Copy is independent of "+"'s availability — shown only one level below a root
                <IconButton
                  k={genericTranslationsK.copy}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    setCopyBtnAnchor(e.currentTarget);
                  }}
                  sx={{ color: theme.palette.customColors.black }}
                >
                  <DekoxIcon icon="copy" fontSize="small" />
                </IconButton>
              )}
              <IconButton k={genericTranslationsK.edit} size="small" onClick={onEdit} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="edit" fontSize="small" />
              </IconButton>
              <IconButton k={genericTranslationsK.delete} size="small" onClick={onDelete} sx={{ color: theme.palette.customColors.darkGrey }}>
                <DekoxIcon icon="delete" fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Menu anchorEl={copyBtnAnchor} open={Boolean(copyBtnAnchor)} onClose={() => setCopyBtnAnchor(null)}>
        <CopyMenu onCopy={handleCopy} maxCount={hasValidCopyLimit ? copyConfigurationItemsLimit : undefined} />
      </Menu>
    </>
  );
};

export const ProductConfiguration = connect(mapStateToProps, ownActions)(ProductConfigurationBase);
